import type { AuthSession } from '@aws-amplify/auth';
import type {
   AccessTokenObjectFields,
   AuthenticatedUser,
   IdTokenObjectFields,
} from '@/types/auth-types.ts';
import type { ApiKey } from '@/types/user/api-keys-types.ts';
import type { DemoRequest, UserProfile } from '@/types/user/user-profile-types.ts';
import { StorageSerializers, useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Store: Auth
 * Manages the auth-state of the user
 */
export const useAuthStore = defineStore('auth', () => {
   const isInitialized = ref(false);

   /*
    * User info
    */
   const accessToken = ref('');
   const accessTokenObject = ref<AccessTokenObjectFields>({});

   const idToken = ref('');
   const idTokenObject = ref<IdTokenObjectFields>({});
   const isAuthenticated = ref(false);

   // access token expiry unix timestamp
   const accessTokenExpiry = ref(0);

   const isTokenExpired = computed(() => {
      return accessTokenExpiry.value < Math.floor(Date.now() / 1000);
   });

   const demoRequest = ref<DemoRequest | null>(null);
   const maintenanceMode = ref<boolean>(false);

   /* ------------------------------------------------------------------------------------------- */

   const user = useStorage<AuthenticatedUser | null>(
      'apaluma-user', // key
      null, // default
      undefined, // storage, undefined for localStorage
      {
         serializer: StorageSerializers.object, // type of serializer
      },
   );

   /*
    * User profile info
    */

   const userProfile = useStorage<UserProfile | null>(
      'apaluma-user-profile', // key
      null, // default
      undefined, // storage, undefined for localStorage
      {
         serializer: StorageSerializers.object, // type of serializer
      },
   );

   const userApiKey = useStorage<ApiKey | null>('apaluma-user-api-key', null, undefined, {
      serializer: StorageSerializers.object,
   });

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Set auth-token from the session info
    */
   const setToken = (session: AuthSession) => {
      // set the access token
      accessTokenObject.value = session.tokens?.accessToken.payload || {};
      accessToken.value = session.tokens?.accessToken.toString() || '';

      accessTokenExpiry.value = accessTokenObject.value.exp || 0;

      // set the id token
      idTokenObject.value = session.tokens?.idToken?.payload || {};
      idToken.value = session.tokens?.idToken?.toString() || '';
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Init auth store
    */
   const init = async () => {
      try {
         const { fetchAuthSession, getCurrentUser } = await import('@aws-amplify/auth');
         const session = await fetchAuthSession();

         if (session.tokens === undefined) {
            isInitialized.value = false;
            return;
         }
         else {
            setToken(session);
         }

         if (await getCurrentUser()) {
            isAuthenticated.value = true;
         }

         isInitialized.value = true;
      }
      catch (e) {
         console.error(e);
         isInitialized.value = true;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Login using username and password
    */
   const passwordLogin = async (username: string, password: string) => {
      const { signIn } = await import('@aws-amplify/auth');
      const { nextStep } = await signIn({ username, password });

      switch (nextStep.signInStep) {
         case 'CONFIRM_SIGN_UP':
            // User needs to confirm their account
            return {
               step: 'CONFIRM_SIGN_UP',
               message: 'Please check your email for confirmation code',
            };

         case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            // MFA via SMS is required
            return {
               step: 'CONFIRM_SIGN_IN_WITH_SMS_CODE',
               message: 'Please enter the code sent to your phone',
            };

         case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
            // MFA via TOTP (authenticator app) is required
            return {
               step: 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
               message: 'Please enter the code from your authenticator app',
            };

         case 'DONE':
            // Successfully signed in

            await init();

            return {
               step: 'DONE',
               message: 'Successfully signed in',
            };

         default:
            return {
               step: 'ERROR',
               message: 'Unknown error',
            };
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Call this to confirm sign up when user logs in for the first time,
    * confirmation code can be obtained from the email sent while signing up
    */
   const confirmSignInWithCode = async (confirmationCode: string) => {
      try {
         const { confirmSignIn } = await import('@aws-amplify/auth');
         const { isSignedIn } = await confirmSignIn({ challengeResponse: confirmationCode });

         if (isSignedIn) {
            await init();

            return {
               step: 'DONE',
               message: 'Successfully confirmed sign in',
            };
         }
      }
      catch (e) {
         console.error('Error during sign in confirmation:', e);
         throw e;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   const resetSignUpCode = async (username: string) => {
      try {
         const { resendSignUpCode } = await import('@aws-amplify/auth');
         await resendSignUpCode({ username });
         return {
            step: 'DONE',
            message: 'Successfully resent sign up code',
         };
      }
      catch (e) {
         console.error('Error during resend sign up code:', e);
         throw e;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Start interactive sign-in. All interactive auth is brokered by the
    * helmsdeep hub (ENG-347) — redirect there.
    */
   const signInWithGoogle = async () => {
      try {
         const { redirectToHub } = await import('@/auth/hub.ts');
         redirectToHub();
      }
      catch (e) {
         console.error('Error during sign in:', e);
         throw e;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Handles the hub sign-in redirect (the app's /auth/callback).
    */
   const handleRedirectSignIn = async () => {
      try {
         // Ingest the Cognito tokens the hub delivered in the URL fragment
         // (which strips the fragment), then load the session.
         const { ingestHubTokens } = await import('@/auth/hub.ts');
         const ingested = await ingestHubTokens();
         if (!ingested)
            throw new Error('No hub callback tokens present — sign-in did not complete');
         await init();

         return {
            step: 'DONE',
            message: 'Successfully signed in',
         };
      }
      catch (e) {
         console.error('Error handling redirect sign in:', e);
         throw e;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Sign up user with email and password
    */
   const signUpUser = async (username: string, password: string, email: string) => {
      try {
         const { signUp } = await import('@aws-amplify/auth');
         const { userId, nextStep } = await signUp({
            username,
            password,
            options: {
               userAttributes: {
                  email,
               },
            },
         });

         if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
            return {
               step: 'CONFIRM_SIGN_UP',
               message: 'Please check your email for confirmation code',
               userId,
            };
         }

         return {
            step: 'ERROR',
            message: 'Unexpected signup step',
         };
      }
      catch (error) {
         console.error('Error during sign up:', error);
         throw error;
      }
   };

   /**
    * Confirm sign up with verification code
    */
   const confirmUserSignUp = async (username: string, confirmationCode: string) => {
      try {
         const { confirmSignUp } = await import('@aws-amplify/auth');
         const { isSignUpComplete } = await confirmSignUp({
            username,
            confirmationCode,
         });

         if (isSignUpComplete) {
            return {
               step: 'DONE',
               message: 'Successfully confirmed sign up',
            };
         }

         return {
            step: 'ERROR',
            message: 'Failed to confirm sign up',
         };
      }
      catch (error) {
         console.error('Error during sign up confirmation:', error);
         throw error;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Sign out user and clear any stored values.
    */
   const resetAuthState = () => {
      isAuthenticated.value = false;
      accessTokenObject.value = {};
      accessToken.value = '';
      accessTokenExpiry.value = 0;
      idTokenObject.value = {};
      idToken.value = '';
      user.value = null;
      userProfile.value = null;
      userApiKey.value = null;
      maintenanceMode.value = false;
      isInitialized.value = false;
   };

   const signOut = async () => {
      try {
         // helmsdeep (ENG-347): global sign-out via the hub — clear local state,
         // then the hub clears its cookie and runs Cognito global sign-out.
         const { redirectToHubLogout } = await import('@/auth/hub.ts');
         resetAuthState();
         await redirectToHubLogout();
         return true;
      }
      catch (e) {
         console.error(e);
      }
      return false;
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Initiates the password reset process for a user
    * @param username The username (email) of the user
    */
   const initiateResetPassword = async (username: string) => {
      try {
         const { resetPassword } = await import('@aws-amplify/auth');
         const { nextStep } = await resetPassword({ username });

         if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
            return {
               step: 'CONFIRM_RESET_PASSWORD_WITH_CODE',
               message: 'Please check your email for the verification code',
            };
         }

         return {
            step: 'ERROR',
            message: 'Unexpected reset password step',
         };
      }
      catch (error: any) {
         console.error('Error during password reset initiation:', error);
         throw error;
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Confirms the password reset with verification code and new password
    * @param username The username (email) of the user
    * @param newPassword The new password
    * @param confirmationCode The verification code received via email
    */
   const confirmPasswordReset = async (
      username: string,
      newPassword: string,
      confirmationCode: string,
   ) => {
      try {
         const { confirmResetPassword } = await import('@aws-amplify/auth');
         await confirmResetPassword({
            username,
            newPassword,
            confirmationCode,
         });

         // If no error is thrown, the password reset was successful
         return {
            step: 'DONE',
            message: 'Password reset successful',
         };
      }
      catch (error: any) {
         console.error('Error during password reset confirmation:', error);
         throw error;
      }
   };

   /**
    * Computed property to check if the user is a premium user
    */
   const isPremiumUser = computed(() => {
      return userProfile.value?.user_type === 'premium';
   });

   /**
    * Computed property to check if the user is a public user (basic plan)
    */
   const isPublicUser = computed(() => {
      return !isPremiumUser.value;
   });

   /**
    * Computed property to check if the user is an NMED user
    */
   const isNMEDUser = computed(() => {
      return isPremiumUser.value && userProfile.value?.organization === 'NMED';
   });

   /**
    * Computed property to check if the user is an APALUMA user
    */
   const isApalumaUser = computed(() => {
      return isPremiumUser.value && (userProfile.value?.organization.startsWith('APALUMA'));
   });

   /**
    * Computed property to check if the user is a TEST user
    */
   const isTestUser = computed(() => {
      return isPremiumUser.value && userProfile.value?.organization.startsWith('TEST');
   });

   /**
    * Computed property to check if the user is a Luma Train user
    */
   const isLumaTrainUser = computed(() => {
      return isPremiumUser.value && userProfile.value?.organization === 'NMED Luma Train';
   });

   /**
    * Computed property to check if the user is an NMED admin user
    */
   const isNMEDAdminUser = computed(() => {
      return userProfile.value?.org_id === 'NMED' && userProfile.value?.org_user_role === 'ADMIN';
   });

   const $reset = () => {
      isAuthenticated.value = false;
      accessTokenObject.value = {};
      accessToken.value = '';
      idTokenObject.value = {};
      idToken.value = '';
      accessTokenExpiry.value = 0;
      user.value = null;
      userProfile.value = null;
      userApiKey.value = null;
      maintenanceMode.value = false;
   };

   /* ------------------------------------------------------------------------------------------- */

   return {
      // state
      accessTokenObject,
      accessToken,
      accessTokenExpiry,
      idTokenObject,
      idToken,
      demoRequest,
      maintenanceMode,
      user,
      isAuthenticated,
      isInitialized,
      isPremiumUser,
      isPublicUser,
      isNMEDUser,
      isApalumaUser,
      isTestUser,
      isLumaTrainUser,
      isNMEDAdminUser,
      isTokenExpired,

      // persisted states
      userProfile,
      userApiKey,

      // actions
      init,
      passwordLogin,
      confirmSignInWithCode,
      signInWithGoogle,
      handleRedirectSignIn,
      signOut,
      signUpUser,
      confirmUserSignUp,
      initiateResetPassword,
      confirmPasswordReset,
      resetSignUpCode,
      $reset,
   };
});
